import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  useSearchContentsLazyQuery,
  VersionStates,
  type SearchContentsQuery,
} from '@common/Providers/GraphqlProvider/generated';

import { searchStaticRoutes, type DynamicContent, type StaticRoute } from '@/utils/searchHelper';

// Type mappings for content types with categories
const CONTENT_TYPE_LABELS: Record<string, string> = {
  news: 'Noticias',
  new: 'Noticias', // Mapeo para el tipo "new" del backend
  event: 'Eventos',
  regulation: 'Normativas',
  procedure: 'Trámites',
  program: 'Programas',
  page: 'Páginas',
  pqrsd: 'PQRSD',
  document: 'Documentos',
  documents: 'Documentos',
};

// Category mappings for different content types
const CONTENT_CATEGORIES: Record<string, string> = {
  // Contenido (antes Administrador de Contenido)
  news: 'Contenido',
  new: 'Contenido', // Mapeo para el tipo "new" del backend
  event: 'Contenido',
  events: 'Contenido',
  regulation: 'Contenido',
  regulations: 'Contenido',
  procedure: 'Contenido',
  procedures: 'Contenido',
  document: 'Contenido',
  documents: 'Contenido',
  dynamic_page: 'Contenido',
  dynamic_pages: 'Contenido',
  pqrsd: 'Contenido',
  pqr: 'Contenido',
  action: 'Contenido',

  // Configuración de Página
  institutional_program: 'Configuración de Página',
  institutional_programs: 'Configuración de Página',
  program_config: 'Configuración de Página',
  programs_config: 'Configuración de Página',
  home_page: 'Configuración de Página',
  header_objects: 'Configuración de Página',
  footer_objects: 'Configuración de Página',
  transparency_page: 'Configuración de Página',
  services_page: 'Configuración de Página',
  regulations_page: 'Configuración de Página',
  participates_page: 'Configuración de Página',
  press_room_page: 'Configuración de Página',

  // Micrositios
  microsite: 'Micrositios',
  micro_site: 'Micrositios',
  site: 'Micrositios',
  custom_site: 'Micrositios',
  default_site: 'Micrositios',
  dynamic_site: 'Micrositios',
};

export interface SearchResults {
  static: StaticRoute[];
  dynamic: DynamicContent[];
}

export interface UseSearchContentOptions {
  maxStaticResults?: number;
  maxDynamicResults?: number;
  debounceMs?: number;
}

export const useSearchContent = (options: UseSearchContentOptions = {}) => {
  const { maxStaticResults = 5, maxDynamicResults = 5, debounceMs = 300 } = options;

  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<SearchResults>({ static: [], dynamic: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [debounceTimeout, setDebounceTimeout] = useState<NodeJS.Timeout | null>(null);

  // GraphQL lazy query
  const [searchContents, { data: graphqlData, loading: graphqlLoading }] =
    useSearchContentsLazyQuery();

  // Generate URL based on content type, category, and version state
  const generateContentUrl = useCallback(
    (type: string, title: string, status?: VersionStates): string => {
      const lowerType = type.toLowerCase();

      // Mapeo de tipos de contenido a sus rutas base (listado) y rutas de edición
      const contentRoutes: Record<string, { listRoute: string; editRoute: string }> = {
        new: {
          listRoute: 'news-management',
          editRoute: 'news-management/edit-news',
        },
        event: {
          listRoute: 'events',
          editRoute: 'events/edit-event',
        },
        regulation: {
          listRoute: 'regulation',
          editRoute: 'regulation/edit',
        },
        procedure: {
          listRoute: 'procedures-and-services',
          editRoute: 'procedures-and-services/edit-procedure',
        },
        document: {
          listRoute: 'documents',
          editRoute: 'documents/edit-document',
        },
        documents: {
          listRoute: 'documents',
          editRoute: 'documents/edit-document',
        },

        dynamic_pages: {
          listRoute: 'dynamic-pages',
          editRoute: 'dynamic-pages/edit-page',
        },
        pqrsd: {
          listRoute: 'pqrsd',
          editRoute: 'pqrsd/edit-action',
        },
        // Mantengo algunos aliases comunes por si acaso
        news: {
          listRoute: 'news-management',
          editRoute: 'news-management/edit-news',
        },
        pqr: {
          listRoute: 'pqrsd',
          editRoute: 'pqrsd/edit-action',
        },
        action: {
          listRoute: 'pqrsd',
          editRoute: 'pqrsd/edit-action',
        },
      };

      // Configuración de Página - Rutas específicas (estas siempre permiten edición)
      const pageConfigRoutes: Record<string, string> = {
        institutional_program: 'institutional-programs',
        institutional_programs: 'institutional-programs',
        program_config: 'institutional-programs',
        programs_config: 'institutional-programs',
        home_page: 'home-page',
        header_objects: 'header-objects',
        footer_objects: 'footer-objects',
        transparency_page: 'transparency-page',
        services_page: 'services-page',
        regulations_page: 'regulations',
        participates_page: 'participates-page',
        press_room_page: 'press-room',
      };

      // Verificar si es contenido de gestión (SIEMPRE ir al listado con filtro)
      if (contentRoutes[lowerType]) {
        const { listRoute } = contentRoutes[lowerType];

        // SIEMPRE ir a la ruta de listado con el título como query param para filtrar
        const encodedTitle = encodeURIComponent(title);
        return `/admin/home/content-management/${listRoute}?filter_title=${encodedTitle}`;
      }

      // Verificar si es configuración de página (mantener comportamiento original)
      if (pageConfigRoutes[lowerType]) {
        // Para configuración de página, seguimos usando el title como identificador
        return `/admin/home/page-config/${pageConfigRoutes[lowerType]}/${title}`;
      }

      // Para micrositios
      if (
        lowerType.includes('microsite') ||
        lowerType.includes('micro_site') ||
        lowerType.includes('site') ||
        lowerType.includes('custom_site') ||
        lowerType.includes('default_site')
      ) {
        return `/admin/home/micro-sites/default-sites`;
      }

      // Fallback para programas institucionales que pueden estar en content-management
      if (lowerType.includes('program')) {
        // SIEMPRE ir al listado, sin importar el estado
        return `/admin/home/content-management/institutional-programs-management`;
      }

      // Fallback genérico - SIEMPRE ir al listado con filtro
      const encodedTitle = encodeURIComponent(title);
      return `/admin/home/content-management/${lowerType}?filter_title=${encodedTitle}`;
    },
    []
  );

  // Transform GraphQL data to DynamicContent format
  const dynamicResults = useMemo(() => {
    if (!graphqlData?.contents) return [];

    return graphqlData.contents.map((content: any): DynamicContent => {
      // El campo status puede no estar disponible aún en el backend
      // Cuando esté disponible, será del tipo VersionStates
      const contentStatus = (content as any).status as VersionStates | undefined;

      return {
        id: content.id,
        title: content.title,
        type: content.type as any,
        category:
          CONTENT_CATEGORIES[content.type.toLowerCase()] ||
          CONTENT_TYPE_LABELS[content.type] ||
          'Contenido',
        url: generateContentUrl(content.type, content.title, contentStatus || VersionStates.Draft),
        description: content.description || undefined,
        publishedAt: content.createdAt,
        status: contentStatus || VersionStates.Draft, // Default a Draft hasta que esté disponible
      };
    });
  }, [graphqlData, generateContentUrl]);

  // Perform search
  const performSearch = useCallback(
    async (term: string) => {
      if (!term.trim()) {
        setResults({ static: [], dynamic: [] });
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      try {
        // Get static results
        const staticResults = searchStaticRoutes(term);

        // Execute GraphQL query for dynamic content
        await searchContents({
          variables: {
            includePagination: false,
            pagination: {
              skip: 0,
              take: maxDynamicResults,
            },
            where: {
              title: {
                _contains: term,
              },
              _or: [
                {
                  description: {
                    _contains: term,
                  },
                },
              ],
            },
          },
        });

        // Static results are set immediately
        setResults((prev: SearchResults) => ({
          ...prev,
          static: staticResults.slice(0, maxStaticResults),
        }));
      } catch (error) {
        console.error('Error performing search:', error);
        setResults({ static: searchStaticRoutes(term).slice(0, maxStaticResults), dynamic: [] });
      } finally {
        setIsLoading(false);
      }
    },
    [searchContents, maxStaticResults, maxDynamicResults]
  );

  // Update dynamic results when GraphQL data changes
  useEffect(() => {
    if (dynamicResults.length >= 0) {
      setResults((prev: SearchResults) => ({
        ...prev,
        dynamic: dynamicResults.slice(0, maxDynamicResults),
      }));
    }
  }, [dynamicResults, maxDynamicResults]);

  // Handle search term changes with debounce
  useEffect(() => {
    if (debounceTimeout) {
      clearTimeout(debounceTimeout);
    }

    if (searchTerm) {
      const timeout = setTimeout(() => {
        performSearch(searchTerm);
      }, debounceMs);

      setDebounceTimeout(timeout);
    } else {
      setResults({ static: [], dynamic: [] });
      setIsLoading(false);
    }

    return () => {
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }
    };
  }, [searchTerm, performSearch, debounceMs]);

  const totalResults = results.static.length + results.dynamic.length;
  const loading = isLoading || graphqlLoading;

  return {
    searchTerm,
    setSearchTerm,
    results,
    isLoading: loading,
    totalResults,
    performSearch,
  };
};
